import { useEffect, Fragment, useMemo } from 'react';

import { Box, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { Button } from '@apigrowfinance/pbolt-button';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import {
    Controller,
    DeepMap,
    FieldError,
    FieldValues,
    useFieldArray,
    useFormContext,
} from 'react-hook-form';
import { RiAddLine, RiDeleteBinLine } from 'react-icons/ri';
import { FormattedMessage } from 'react-intl';

import flexControls from '@messages/flexControls/flexControls';
import flexControlsValidations from '@messages/flexControls/flexControlsValidations';
import getEnumTranslation from '@utils/getEnumTranslation';

interface ConditionsSharedProps {
    attributes: string[];
    operators: string[];
    isEditing?: boolean;
}

const ConditionsForm = ({
    attributes,
    operators,
    isEditing,
}: ConditionsSharedProps) => {
    const {
        control,
        watch,
        getValues,
        setValue,
        formState: { errors },
    } = useFormContext();
    const values = getValues();
    const { theme } = useTheme();
    const fieldArrayName = 'conditions';
    const { fields, append } = useFieldArray({
        name: fieldArrayName,
    });
    const watchFieldArray = watch(fieldArrayName);

    const controlledFields = useMemo(() => {
        return !!fields?.length
            ? fields.map((field, index) => {
                  return {
                      ...field,
                      ...watchFieldArray[index],
                  };
              })
            : [];
    }, [fields, watchFieldArray]);

    useEffect(() => {
        if (controlledFields.length === 0 && !isEditing) {
            append({
                attribute: undefined,
                operator: undefined,
                value: undefined,
            });
        }
    }, [append, isEditing, controlledFields, values]);

    return (
        <>
            <Grid container spacing={3} flexDirection="row">
                {!!controlledFields?.length &&
                    controlledFields?.map((field, index) => {
                        return (
                            <Fragment key={index}>
                                <Grid item xs={12}>
                                    <Box
                                        display="flex"
                                        flexDirection="row"
                                        justifyContent="space-between">
                                        <Typography
                                            variant={isEditing ? 'h6' : 'h5'}
                                            sx={{
                                                color:
                                                    theme?.palette?.brand
                                                        ?.textMain ||
                                                    'text.primary',
                                                mb: 2,
                                            }}>
                                            <FormattedMessage
                                                id="Conditions.condition"
                                                defaultMessage="Condition {conditionAmount}"
                                                values={{
                                                    conditionAmount: index + 1,
                                                }}
                                            />
                                        </Typography>
                                        {(index > 0 || isEditing) && (
                                            <Box
                                                sx={{
                                                    '&:hover': {
                                                        cursor: 'pointer',
                                                    },
                                                }}
                                                onClick={() => {
                                                    const newArray =
                                                        controlledFields.filter(
                                                            (
                                                                _field,
                                                                fieldIndex,
                                                            ) => {
                                                                return (
                                                                    fieldIndex !==
                                                                    index
                                                                );
                                                            },
                                                        );
                                                    setValue(
                                                        'conditions',
                                                        newArray,
                                                        {
                                                            shouldDirty: true,
                                                        },
                                                    );
                                                }}
                                                display="flex"
                                                flexDirection="row"
                                                justifyContent="space-around"
                                                alignItems="center">
                                                <RiDeleteBinLine
                                                    color={
                                                        theme?.palette?.error
                                                            ?.dark
                                                    }
                                                />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        ml: 0.5,
                                                        color: theme?.palette
                                                            ?.error?.dark,
                                                    }}>
                                                    {getEnumTranslation(
                                                        'delete',
                                                    )}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={isEditing ? 12 : 4}>
                                    <Controller
                                        name={`${fieldArrayName}[${index}][attribute]`}
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <TextField
                                                data-testid="attribute"
                                                select
                                                onChange={onChange}
                                                value={value}
                                                fullWidth
                                                error={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]
                                                        ?.attribute?.message
                                                }
                                                helperText={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]
                                                        ?.attribute
                                                        ?.message && (
                                                        <Box component="span">
                                                            {getEnumTranslation(
                                                                (
                                                                    errors as DeepMap<
                                                                        FieldValues,
                                                                        FieldError
                                                                    >
                                                                ).conditions?.[
                                                                    index
                                                                ]?.attribute?.message.toString(),
                                                                {
                                                                    messages:
                                                                        flexControlsValidations,
                                                                },
                                                            )}
                                                        </Box>
                                                    )
                                                }
                                                label={
                                                    <FormattedMessage
                                                        id="Conditions.attribute"
                                                        defaultMessage="Attribute"
                                                    />
                                                }>
                                                {attributes.map(
                                                    (attribute: string) => {
                                                        return (
                                                            <MenuItem
                                                                key={attribute}
                                                                value={
                                                                    attribute
                                                                }>
                                                                {getEnumTranslation(
                                                                    attribute,
                                                                    {
                                                                        messages:
                                                                            flexControls,
                                                                    },
                                                                )}
                                                            </MenuItem>
                                                        );
                                                    },
                                                )}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={isEditing ? 12 : 4}>
                                    <Controller
                                        name={`${fieldArrayName}[${index}][operator]`}
                                        control={control}
                                        render={({
                                            field: { onChange, value },
                                        }) => (
                                            <TextField
                                                data-testid="operator"
                                                select
                                                onChange={onChange}
                                                value={value}
                                                fullWidth
                                                error={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]
                                                        ?.operator?.message
                                                }
                                                helperText={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]
                                                        ?.operator?.message && (
                                                        <Box component="span">
                                                            {getEnumTranslation(
                                                                (
                                                                    errors as DeepMap<
                                                                        FieldValues,
                                                                        FieldError
                                                                    >
                                                                ).conditions?.[
                                                                    index
                                                                ]?.operator?.message.toString(),
                                                                {
                                                                    messages:
                                                                        flexControlsValidations,
                                                                },
                                                            )}
                                                        </Box>
                                                    )
                                                }
                                                label={
                                                    <FormattedMessage
                                                        id="Conditions.operator"
                                                        defaultMessage="Operator"
                                                    />
                                                }>
                                                {operators.map((operator) => {
                                                    return (
                                                        <MenuItem
                                                            key={operator}
                                                            value={operator}>
                                                            {getEnumTranslation(
                                                                operator,
                                                                {
                                                                    messages:
                                                                        flexControls,
                                                                },
                                                            )}
                                                        </MenuItem>
                                                    );
                                                })}
                                            </TextField>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={isEditing ? 12 : 4}>
                                    <Controller
                                        name={`${fieldArrayName}[${index}][value]`}
                                        control={control}
                                        render={({ field }) => (
                                            <TextField
                                                data-testid="value"
                                                {...field}
                                                label={
                                                    <FormattedMessage
                                                        id="Conditions.value"
                                                        defaultMessage="Value"
                                                    />
                                                }
                                                fullWidth
                                                error={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]?.value
                                                        ?.message
                                                }
                                                helperText={
                                                    !!(
                                                        errors as DeepMap<
                                                            FieldValues,
                                                            FieldError
                                                        >
                                                    ).conditions?.[index]?.value
                                                        ?.message && (
                                                        <Box component="span">
                                                            {getEnumTranslation(
                                                                (
                                                                    errors as DeepMap<
                                                                        FieldValues,
                                                                        FieldError
                                                                    >
                                                                ).conditions?.[
                                                                    index
                                                                ]?.value?.message.toString(),
                                                                {
                                                                    messages:
                                                                        flexControlsValidations,
                                                                },
                                                            )}
                                                        </Box>
                                                    )
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            </Fragment>
                        );
                    })}
            </Grid>
            <Box display="flex" flexDirection="row" justifyContent="flex-end">
                <Button
                    onClick={() => {
                        // Append a new empty object to the field array
                        append({
                            attribute: undefined,
                            operator: undefined,
                            value: undefined,
                            id: undefined,
                        });
                    }}
                    sx={{
                        ml: 0,
                        borderRadius: 0.5,
                        mt: controlledFields.length === 0 ? 2 : 0,
                    }}
                    startIcon={<RiAddLine />}
                    variant="outlined"
                    disableElevation
                    fullWidth
                    role="button">
                    <FormattedMessage
                        id="Conditions.addCondition"
                        defaultMessage="Add condition"
                    />
                </Button>
            </Box>
        </>
    );
};

export default ConditionsForm;
